/**
 * Dev mode → Guide tests: runs the guide's test set (guideCases.ts) in the real app, on the person's own data, one
 * ask after another — the same cases the backend evals run, now through the real pages and the real router.
 *
 * Safe by construction: its own conversation thread, decks are not built (dry run), and every "Approve…?" is
 * answered no. Results stay in this browser (localStorage) and can be downloaded as JSON.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Loader2, Play, Square } from 'lucide-react';
import { parseAppPath } from '@/routes';
import { useGuide, type GuideTurnRecord } from './GuideContext';
import { GUIDE_CASES, type GuideCase } from './guideCases';
import { entryFor, pathFor } from './pageMap';

const RESULTS_KEY = 'guide-test-results-v1';
interface CaseResult { id: string; ask: string; verb: string; expect: string[]; landed: string; pass: boolean; note?: string; route?: string; firstNavMs?: number; doneMs?: number; said?: string; path?: string; confirms?: string[] }

/** Where a case should start: a page reached by URL, or by asking first (campaign tabs need a real campaign). */
const SETUP: Record<string, string> = {
  'campaign-results': 'Results of my latest campaign',
  'campaign-contentApproval': 'What is waiting for approval in my latest campaign?',
};
const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function GuideTestRunner() {
  const g = useGuide();
  const navigate = useNavigate();
  const [running, setRunning] = useState(false);
  const [stop, setStop] = useState(false);
  const [current, setCurrent] = useState<string | null>(null);
  const [results, setResults] = useState<CaseResult[]>(() => { try { return JSON.parse(localStorage.getItem(RESULTS_KEY) || '[]'); } catch { return []; } });
  if (!g?.available) return null;

  const landedOf = (rec: GuideTurnRecord): string => {
    const loc = window.location;
    const p = parseAppPath(loc.pathname);
    const entry = entryFor(loc.pathname, p.currentPage, p.subTab);
    if (!rec.navigations.length) return !rec.actions.length && /\?\s*$/.test(rec.said || '') ? 'clarify' : 'here';
    return entry?.id || p.currentPage;
  };

  async function runCase(c: GuideCase, session: string): Promise<CaseResult> {
    const base = { id: c.id, ask: c.ask, verb: c.verb, expect: c.expect };
    if (c.here === 'reports' || c.here === 'video-studio') return { ...base, landed: 'skipped', pass: false, note: 'Needs an open deck/video: run it by hand from one.' };
    // Stand where the case starts.
    if (c.here && SETUP[c.here]) {
      const s = await g!.send(SETUP[c.here], 'chat', { session, dryRun: true, declineConfirms: true });
      if (!s.navigations.length) return { ...base, landed: 'setup failed', pass: false, note: `Setup "${SETUP[c.here]}" did not open a page.` };
    } else {
      navigate(pathFor(c.here || 'dashboard') || '/');
    }
    await wait(1800); // let the page render what the guide reads from the screen
    const rec = await g!.send(c.ask, 'chat', { session, dryRun: true, declineConfirms: true });
    await wait(1200);
    const landed = landedOf(rec);
    const pass = c.expect.includes(landed);
    return { ...base, landed, pass, route: rec.route ? `${rec.route.page} ${Math.round(rec.route.p * 100)}%` : undefined, firstNavMs: rec.timing.firstNavMs, doneMs: rec.timing.doneMs, said: rec.said, path: `${window.location.pathname}${window.location.search}`, confirms: rec.confirms, note: rec.error };
  }

  async function runAll(only?: GuideCase[]) {
    setRunning(true); setStop(false);
    const out: CaseResult[] = [];
    for (const c of only || GUIDE_CASES) {
      if (stopRef.stop) break;
      setCurrent(c.id);
      try { out.push(await runCase(c, `test-${c.id}-${Date.now()}`)); }
      catch (e) { out.push({ id: c.id, ask: c.ask, verb: c.verb, expect: c.expect, landed: 'error', pass: false, note: (e as Error).message }); }
      const merged = [...results.filter((r) => !out.some((o) => o.id === r.id)), ...out];
      setResults(merged);
      try { localStorage.setItem(RESULTS_KEY, JSON.stringify(merged)); } catch { /* storage off */ }
    }
    setCurrent(null); setRunning(false); stopRef.stop = false;
    navigate('/dev-mode');
  }

  const passed = results.filter((r) => r.pass).length;
  const download = () => {
    const blob = new Blob([JSON.stringify({ at: new Date().toISOString(), results }, null, 1)], { type: 'application/json' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `guide-tests-${new Date().toISOString().slice(0, 16)}.json`; a.click();
  };

  return (
    <section className="mb-8 rounded-2xl border border-[#be43ff]/25 bg-[#be43ff]/[0.03] p-5" data-guide-tests>
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-col">
          <h2 className="text-[16px] font-semibold text-[#101828]">Guide tests</h2>
          <p className="text-[12.5px] text-[#667085]">{GUIDE_CASES.length} asks through Lykke on your own data: its own thread, no decks built, every approval answered no.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {results.length > 0 && <span className="text-[13px] font-semibold text-[#344054]">{passed}/{results.length} pass</span>}
          {running
            ? <button type="button" onClick={() => { stopRef.stop = true; setStop(true); }} className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 px-3 py-1.5 text-[12.5px] font-semibold"><Square className="h-3.5 w-3.5" />{stop ? 'Stopping…' : 'Stop'}</button>
            : <button type="button" onClick={() => void runAll()} className="inline-flex items-center gap-1.5 rounded-full bg-[#101828] px-3.5 py-1.5 text-[12.5px] font-semibold text-white"><Play className="h-3.5 w-3.5" />Run all</button>}
          {results.length > 0 && !running && <button type="button" onClick={() => void runAll(GUIDE_CASES.filter((c) => !results.find((r) => r.id === c.id)?.pass))} className="rounded-full border border-gray-300 px-3 py-1.5 text-[12.5px] font-semibold">Run failed again</button>}
          {results.length > 0 && <button type="button" onClick={download} className="inline-flex items-center gap-1 rounded-full border border-gray-300 px-3 py-1.5 text-[12.5px] font-semibold"><Download className="h-3.5 w-3.5" />JSON</button>}
        </div>
      </div>
      {running && <div className="mt-3 flex items-center gap-2 text-[12.5px] text-[#7a1fbf]"><Loader2 className="h-3.5 w-3.5 animate-spin" />Running {current}…</div>}
      {results.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[860px] text-left text-[12.5px]">
            <thead className="text-[11px] uppercase tracking-[0.06em] text-[#98a2b3]"><tr><th className="py-1.5 pr-3">Case</th><th className="pr-3">Ask</th><th className="pr-3">Landed</th><th className="pr-3">Page change</th><th className="pr-3">Done</th><th>Lykke said</th></tr></thead>
            <tbody>
              {GUIDE_CASES.map((c) => results.find((r) => r.id === c.id)).filter((r): r is CaseResult => !!r).map((r) => (
                <tr key={r.id} className="border-t border-gray-100 align-top">
                  <td className="py-1.5 pr-3"><span className={`mr-1.5 inline-block h-2 w-2 rounded-full ${r.pass ? 'bg-[#12b76a]' : 'bg-[#f04438]'}`} />{r.id}</td>
                  <td className="pr-3 text-[#344054]">{r.ask}</td>
                  <td className="pr-3"><span className="font-semibold">{r.landed}</span>{!r.pass && <span className="block text-[11px] text-[#98a2b3]">want {r.expect.join(' | ')}</span>}</td>
                  <td className="pr-3 tabular-nums">{r.firstNavMs !== undefined ? `${(r.firstNavMs / 1000).toFixed(1)} s` : '—'}</td>
                  <td className="pr-3 tabular-nums">{r.doneMs !== undefined ? `${(r.doneMs / 1000).toFixed(1)} s` : '—'}</td>
                  <td className="text-[#101828]">{r.said || <span className="text-[#98a2b3]">(nothing)</span>}{r.note && <span className="block text-[11px] text-[#b42318]">{r.note}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Stop flag read inside the async loop (state would be stale there). */
const stopRef = { stop: false };
